import { API_URL } from '../config';
import logger from './logger';

/**
 * Typed error thrown by apiCallJson. The HTTP status + the optional
 * server-provided `code` field let callers branch on what happened without
 * parsing French error messages.
 *
 * Use `instanceof ApiError` in catch blocks; `status` and `code` are the
 * stable identifiers, `message` is for the user, `data` carries any
 * additional payload the server included.
 */
export class ApiError extends Error {
  constructor({ status, message, code, data }) {
    super(message || 'Erreur réseau');
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

/**
 * Lightweight event bus for auth lifecycle signals coming out of api.js.
 *
 * Why this exists: the previous design called `window.location.href = '/auth'`
 * directly from inside the fetch wrapper. That fights with react-router (it
 * unmounts the whole tree, killing in-flight optimistic updates) and made
 * the auth flow impossible to test without a real browser. AuthProvider
 * subscribes to 'unauthenticated' here and drives a clean navigate().
 *
 * Events:
 *   'unauthenticated' — session no longer valid (401 after refresh attempt
 *                       failed, or refresh threw). localStorage has already
 *                       been cleared by api.js by the time the event fires.
 */
export const authEvents = typeof EventTarget !== 'undefined' ? new EventTarget() : null;

const emitAuthEvent = (name) => {
  if (authEvents) {
    try { authEvents.dispatchEvent(new Event(name)); } catch { /* never throw from fire-and-forget */ }
  }
};

// Secure API helper with automatic token handling
export const apiCall = async (url, options = {}) => {
  // If url is a relative path, prepend API_URL
  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  const token = localStorage.getItem('token');
  const method = options.method || 'GET';
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  
  // Log API call without exposing token
  logger.api(method, url, null, { 
    fullUrl: fullUrl.replace(/\/api\/auth\/(login|verify|refresh)/, '/api/auth/[REDACTED]')
  });
  
  const headers = {
    ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  // Handle token expiration (401) - try to refresh once; do not refresh on 403 (forbidden)
  // Skip auto-refresh retry for multipart: body stream may not be re-readable
  if (response.status === 401 && token && !fullUrl.includes('/auth/refresh') && !isFormData) {
    try {
      logger.debug('Token expired, attempting refresh');
      const refreshResponse = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        localStorage.setItem('token', refreshData.token);
        localStorage.setItem('user', JSON.stringify(refreshData.user));
        logger.info('Token refreshed successfully');
        
        // Retry original request with new token
        headers['Authorization'] = `Bearer ${refreshData.token}`;
        response = await fetch(fullUrl, {
          ...options,
          headers,
        });
      } else {
        // Refresh failed — clear auth and signal the AuthProvider to drive
        // the navigate() (no more hard window.location reset).
        logger.warn('Token refresh failed, clearing auth and signaling unauthenticated');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        emitAuthEvent('unauthenticated');
        return response; // Return the failed response
      }
    } catch (error) {
      logger.error('Token refresh failed', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      emitAuthEvent('unauthenticated');
      return response; // Return the failed response
    }
  }

  // On 401 (invalid/expired token), clear session and signal AuthProvider.
  // On 403 (forbidden), do NOT disconnect: let the caller show the error so the user can stay logged in.
  if (response.status === 401 &&
      !fullUrl.includes('/auth/login') &&
      !fullUrl.includes('/auth/verify') &&
      !fullUrl.includes('/auth/refresh') &&
      window.location.pathname !== '/auth' &&
      token) {
    logger.warn('Unauthorized (401), signaling unauthenticated');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    emitAuthEvent('unauthenticated');
  }

  // Log API response without exposing sensitive data
  logger.api(method, url, response.status, { 
    ok: response.ok,
    statusText: response.statusText,
    url: url.replace(/\/api\/auth\/(login|verify|refresh)/, '/api/auth/[REDACTED]')
  });

  return response;
};

/**
 * JSON-throwing wrapper around apiCall. Parses the body, throws ApiError on
 * a non-OK response OR on `{ success: false }`, and returns the parsed body
 * on success.
 *
 *   try {
 *     const data = await apiCallJson('/api/foo');
 *     // data.success is true; use data.foo etc.
 *   } catch (e) {
 *     if (e instanceof ApiError) setError(e.message); // status/code in e
 *   }
 *
 * Keep using raw `apiCall` for endpoints that return non-JSON (file
 * downloads, SSE streams) or where the caller wants direct access to the
 * Response object (multipart upload progress, custom 4xx handling).
 *
 * 401 handling is unchanged from apiCall: the underlying fetch wrapper
 * still emits 'unauthenticated' on the bus when appropriate. This wrapper
 * just turns the resulting non-OK response into a throw.
 */
export async function apiCallJson(url, options = {}) {
  const response = await apiCall(url, options);
  let data = null;
  const ct = response.headers?.get?.('content-type') || '';
  if (ct.includes('application/json')) {
    try { data = await response.json(); } catch { data = null; }
  }
  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      message: data?.message || `Erreur ${response.status}`,
      code: data?.code,
      data,
    });
  }
  // The backend convention is `{ success: true|false, ...}` even on 2xx.
  // Treat success:false as an error so callers don't need to re-check.
  if (data && data.success === false) {
    throw new ApiError({
      status: response.status,
      message: data.message || 'Échec de la requête',
      code: data.code,
      data,
    });
  }
  return data || {};
}

// Step Templates API — uses apiCallJson so callers get ApiError on failure
// (with status + code) instead of generic Error('Failed to …').
export const stepTemplatesAPI = {
  async getTemplates() {
    const data = await apiCallJson('/api/production/step-templates', { method: 'GET' });
    return data.templates || [];
  },

  async getTemplateById(id) {
    const data = await apiCallJson(`/api/production/step-templates/${id}`, { method: 'GET' });
    return data.template || null;
  },

  async createTemplate(name, steps, description = '') {
    const data = await apiCallJson('/api/production/step-templates', {
      method: 'POST',
      body: JSON.stringify({ name, steps, description }),
    });
    return data.template;
  },

  async updateTemplate(id, updates) {
    const data = await apiCallJson(`/api/production/step-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return data.template;
  },

  async deleteTemplate(id) {
    await apiCallJson(`/api/production/step-templates/${id}`, { method: 'DELETE' });
    return true;
  },
};

// Field Templates API — same migration shape as stepTemplatesAPI.
export const fieldTemplatesAPI = {
  async getTemplates() {
    const data = await apiCallJson('/api/production/field-templates', { method: 'GET' });
    return data.templates || [];
  },

  async getTemplateById(id) {
    const data = await apiCallJson(`/api/production/field-templates/${id}`, { method: 'GET' });
    return data.template || null;
  },

  async createTemplate(name, fields, description = '') {
    const data = await apiCallJson('/api/production/field-templates', {
      method: 'POST',
      body: JSON.stringify({ name, fields, description }),
    });
    return data.template;
  },

  async updateTemplate(id, updates) {
    const data = await apiCallJson(`/api/production/field-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return data.template;
  },

  async deleteTemplate(id) {
    await apiCallJson(`/api/production/field-templates/${id}`, { method: 'DELETE' });
    return true;
  },
};

